import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Users, Wallet, ArrowRight, CalendarRange } from 'lucide-react';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler } from 'chart.js';
import api from '../../utils/api';
import { inr } from '../../utils/format';
import {
    aggregateClientPortfolio,
    aggregateReimbursements,
    aggregateDashboardFinancials,
    buildActivityFeed,
    buildCelebrations,
    topPerformers,
    monthlyClientTrend,
    filterSubmissions,
    fmtUpdatedLabel,
} from '../../utils/dashboardMetrics';
import {
    DashboardFilters,
    FinancialSummarySection,
    BreakdownCard,
    RecentActivityCard,
    CelebrationsCard,
    TrendLineChart,
    CollectionRateCard,
    TopPerformersSection,
    CLIENT_STATUS_COLOR,
} from '../../components/dashboard/DashboardWidgets';
import LiveSheetBoard from '../../components/dashboard/LiveSheetBoard';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

const STATUS_PIE = [
    { key: 'approved', label: 'Approved' },
    { key: 'submitted', label: 'Pending' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'draft', label: 'Draft' },
];

function clientStatusPie(counts) {
    return STATUS_PIE.map(s => ({
        label: s.label,
        value: counts[s.key] || 0,
        color: CLIENT_STATUS_COLOR[s.key],
    }));
}

function collectionPie(portfolio) {
    return [
        { label: 'Collected', value: portfolio.totalCollected, color: '#1a9a88' },
        { label: 'Balance', value: portfolio.totalBalance, color: '#d48484' },
    ];
}

export default function AdminDashboard() {
    const [reimb, setReimb] = useState([]);
    const [users, setUsers] = useState([]);
    const [subs, setSubs] = useState([]);
    const [openTasks, setOpenTasks] = useState(0);
    const [period, setPeriod] = useState('6m');
    const [status, setStatus] = useState('all');
    const [showDailyMeetings, setShowDailyMeetings] = useState(false);

    useEffect(() => {
        api.get('/reimbursements/admin').then(r => setReimb(r.data.items || [])).catch(() => {});
        api.get('/users').then(r => setUsers(r.data.items || [])).catch(() => {});
        api.get('/forms/submissions').then(r => setSubs(r.data.items || [])).catch(() => {});
        api.get('/admin-tasks?status=open').then(r => setOpenTasks((r.data.items || []).length)).catch(() => {});
    }, []);

    const filteredSubs = useMemo(() => filterSubmissions(subs, { period, status }), [subs, period, status]);
    const portfolio = useMemo(() => aggregateClientPortfolio(filteredSubs), [filteredSubs]);
    const financials = useMemo(() => aggregateDashboardFinancials(filteredSubs), [filteredSubs]);
    const reimbStats = useMemo(() => aggregateReimbursements(reimb), [reimb]);
    const trend = useMemo(() => monthlyClientTrend(filteredSubs), [filteredSubs]);
    const performers = useMemo(() => topPerformers(filteredSubs, 5), [filteredSubs]);
    const monthlyPerformers = useMemo(() => {
        const start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        return topPerformers(filteredSubs.filter(s => new Date(s.createdAt) >= start), 5);
    }, [filteredSubs]);
    const activity = useMemo(() => buildActivityFeed({ subs, reimb, users, limit: 5 }), [subs, reimb, users]);
    const celebrations = useMemo(
        () => buildCelebrations({ users, subs: filteredSubs, clientBasePath: '/admin/clients' }),
        [users, filteredSubs],
    );

    const pendingUsers = users.filter(u => u.status === 'pending').length;
    const pendingTotal = pendingUsers + portfolio.pendingReview + portfolio.pendingPayments + (reimbStats.counts.submitted || 0) + openTasks;

    const pendingItems = [
        { label: 'User signups', count: pendingUsers },
        { label: 'Client reviews', count: portfolio.pendingReview },
        { label: 'Payments', count: portfolio.pendingPayments },
        { label: 'Onboarding calls', count: openTasks },
        { label: 'Expenses', count: reimbStats.counts.submitted || 0 },
    ].filter(i => i.count > 0);

    if (showDailyMeetings) {
        return (
            <LiveSheetBoard onBack={() => setShowDailyMeetings(false)} />
        );
    }

    return (
        <div className="space-y-4 pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <DashboardFilters period={period} onPeriodChange={setPeriod} status={status} onStatusChange={setStatus} />
                    <span className="text-[10px] text-ink-400 hidden sm:inline">{fmtUpdatedLabel()}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setShowDailyMeetings(true)}
                        className="ag-btn text-xs py-1.5"
                    >
                        <CalendarRange className="h-3.5 w-3.5" />
                        Daily meetings data
                    </button>
                    <Link to="/admin/kpi-analytics" className="ag-btn-ghost text-xs py-1.5">
                        Performance <ArrowRight className="h-3 w-3" />
                    </Link>
                    <Link to="/admin/approvals" className="ag-btn-primary text-xs py-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Approvals{pendingTotal > 0 ? ` (${pendingTotal})` : ''}
                    </Link>
                </div>
            </div>

            <FinancialSummarySection
                financials={financials}
                subtitle="Aggregated across all active franchise clients"
                overdueLink="/admin/payment-ledger"
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch min-h-[360px]">
                <BreakdownCard
                    title="Portfolio breakdown"
                    sections={[
                        { title: 'Client status', segments: clientStatusPie(portfolio.counts), icon: Users },
                        { title: 'Collection split', segments: collectionPie(portfolio), icon: Wallet, formatValue: inr },
                    ]}
                />
                <RecentActivityCard
                    items={activity}
                    linkTo="/admin/approvals"
                    linkLabel="All notifications"
                />
                <CelebrationsCard events={celebrations} showTeamTab />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch min-h-[280px]">
                <TrendLineChart title="Clients over time" trend={trend} />
                <CollectionRateCard
                    rate={portfolio.collectionRate}
                    portfolio={portfolio}
                    pendingItems={pendingItems}
                    pendingLink="/admin/approvals"
                />
            </div>

            <TopPerformersSection performers={performers} monthlyPerformers={monthlyPerformers} />
        </div>
    );
}
